 
/**
 * S13 · Form Overview — section-level progress for the template that's
 * attached to this session. Tap a section row to jump into the first
 * unfilled field (or the next required one) in that section.
 */
import { useMemo } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  Btn,
  Chip,
  GestureBar,
  I,
  Ico,
  ScreenFrame,
  StatusBar,
  T,
} from '@/components/primitives';
import { useFieldValues, useSession } from '@kapture/client-core';
import { fieldsForSection, getTemplate } from '@kapture/domain';
import { LayoutOrLeaf } from '@/components/LayoutOrLeaf';

export const Route = createFileRoute('/sessions/$sessionId/form')({
  component: FormOverviewPage,
  staticData: { mobile: true },
});

function FormOverviewPage() {
  return (
    <LayoutOrLeaf routeId="/sessions/$sessionId/form">
      <FormOverviewContent />
    </LayoutOrLeaf>
  );
}

function FormOverviewContent() {
  const { sessionId } = Route.useParams();
  const navigate = useNavigate();
  const { data: session } = useSession(sessionId);
  const { data: values } = useFieldValues(sessionId);

  const template = getTemplate(session?.templateId);
  const filledIds = useMemo(() => new Set((values ?? []).map((v) => v.fieldId)), [values]);

  if (!session) {
    return (
      <ScreenFrame tone="light">
        <StatusBar />
        <Header sessionId={sessionId} title="Form" subtitle="Loading…" />
      </ScreenFrame>
    );
  }

  if (!template) {
    return (
      <ScreenFrame tone="light">
        <StatusBar />
        <Header sessionId={sessionId} title="Form" subtitle="No template attached" />
        <div
          style={{
            flex: 1,
            padding: 24,
            color: T.textDim,
            fontFamily: T.font,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          This session doesn't have a template attached yet. Attach one from{' '}
          <Link to="/templates" style={{ color: T.brand, fontWeight: 600, textDecoration: 'none' }}>
            Templates
          </Link>{' '}
          to unlock the form flow.
        </div>
      </ScreenFrame>
    );
  }

  const totalFields = template.fields.length;
  const filledCount = template.fields.filter((f) => filledIds.has(f.id)).length;
  const progress = totalFields === 0 ? 0 : filledCount / totalFields;

  // Resolved to definitions rather than rendered as raw ids: a reviewer flags
  // `defect-width-mm` and the inspector needs to read "Defect width". An id
  // the template no longer has simply drops out, which is the right outcome —
  // a template can change between the review and the fix.
  const flaggedDefs = (session?.flaggedFields ?? [])
    .map((id) => (template ? template.fields.find((f) => f.id === id) : undefined))
    .filter((f): f is NonNullable<typeof f> => Boolean(f));

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <Header
        sessionId={sessionId}
        title={template.name}
        subtitle={`v${template.version} · ${filledCount}/${totalFields} fields`}
      />

      <div style={{ padding: 16 }}>
        <div
          style={{
            padding: 14,
            borderRadius: 12,
            background: T.ink,
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Ico d={I.sparkle} size={14} fill={T.brand} stroke={T.brand} />
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '-0.01em', fontFamily: T.font }}>
              {progress === 1 ? 'Form ready for review' : `${Math.round(progress * 100)}% filled`}
            </div>
          </div>
          <div
            style={{
              height: 6,
              borderRadius: 3,
              background: 'rgba(255,255,255,0.1)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.round(progress * 100)}%`,
                height: '100%',
                background: T.brand,
                transition: 'width 120ms ease',
              }}
            />
          </div>
          <div style={{ fontSize: 10, fontFamily: T.mono, color: 'rgba(255,255,255,0.7)' }}>
            {filledCount} of {totalFields} · {template.sections.length} sections
          </div>
        </div>

        {/*
          What the reviewer sent back, and which fields they meant.
          The note is the reason; the list is what you can act on. Shown here
          rather than only on the submissions screen because this is where the
          inspector is when they go to fix it.
        */}
        {session.status === 'changes_requested' && (
          <div
            style={{
              marginTop: 10,
              padding: 12,
              borderRadius: 12,
              background: T.warnSoft,
              border: `1px solid ${T.warnSoft}`,
              color: T.warn,
              fontSize: 13,
              fontFamily: T.font,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Ico d={I.warn} size={14} stroke={T.warn} sw={2.2} />
              <b style={{ fontWeight: 700 }}>Sent back for a fix</b>
            </div>
            <div>
              {session.reviewNote ??
                'No note was left. Ask the reviewer what they need.'}
            </div>
            {flaggedDefs.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {flaggedDefs.map((f) => (
                  <Link
                    key={f.id}
                    to="/sessions/$sessionId/form/$fieldId"
                    params={{ sessionId, fieldId: f.id }}
                    style={{
                      padding: '5px 10px',
                      borderRadius: 100,
                      background: T.surface,
                      border: `1px solid ${T.warn}`,
                      color: T.warn,
                      fontSize: 11,
                      fontWeight: 700,
                      textDecoration: 'none',
                    }}
                  >
                    {f.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 16px' }}>
        {template.sections
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((section, idx) => {
            const fields = fieldsForSection(template, section.id);
            const done = fields.filter((f) => filledIds.has(f.id)).length;
            const total = fields.length;
            const state = done === total ? 'done' : done > 0 ? 'active' : 'todo';
            const nextField = fields.find((f) => !filledIds.has(f.id)) ?? fields[0];
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => {
                  if (!nextField) return;
                  navigate({
                    to: '/sessions/$sessionId/form/$fieldId',
                    params: { sessionId, fieldId: nextField.id },
                  });
                }}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  marginBottom: 8,
                  padding: 14,
                  background: T.surface,
                  border: `1px solid ${state === 'active' ? T.brand : T.stroke}`,
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  cursor: fields.length ? 'pointer' : 'not-allowed',
                  boxShadow: state === 'active' ? '0 0 0 3px rgba(255,90,31,0.08)' : 'none',
                  fontFamily: T.font,
                }}
              >
                <SectionBadge state={state} index={idx} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.01em' }}>
                    {section.title}
                  </div>
                  <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.mono, marginTop: 2 }}>
                    {done} of {total} · {state === 'done' ? 'Complete' : state === 'active' ? 'In progress' : 'Not started'}
                  </div>
                </div>
                {state === 'done' ? (
                  <Chip tone="good" size="sm">
                    Done
                  </Chip>
                ) : (
                  <Ico d={I.chev} size={16} stroke={T.textMute} />
                )}
              </button>
            );
          })}
      </div>

      <div
        style={{
          padding: '10px 16px 12px',
          borderTop: `1px solid ${T.stroke}`,
          display: 'flex',
          gap: 8,
        }}
      >
        <Btn
          tone="light"
          size="md"
          icon={<Ico d={I.back} size={14} />}
          onClick={() =>
            navigate({ to: '/sessions/$sessionId', params: { sessionId } })
          }
        >
          Timeline
        </Btn>
        <Btn
          tone="primary"
          size="md"
          full
          iconRight={<Ico d={I.arrow} size={16} />}
          onClick={() => {
            const next = template.fields.find((f) => !filledIds.has(f.id));
            if (!next) return;
            navigate({
              to: '/sessions/$sessionId/form/$fieldId',
              params: { sessionId, fieldId: next.id },
            });
          }}
          disabled={progress === 1}
          style={{ opacity: progress === 1 ? 0.5 : 1 }}
        >
          {progress === 1 ? 'All filled' : 'Continue filling'}
        </Btn>
      </div>
      <GestureBar />
    </ScreenFrame>
  );
}

function Header({
  sessionId,
  title,
  subtitle,
}: {
  sessionId: string;
  title: string;
  subtitle: string;
}) {
  return (
    <div
      style={{
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        borderBottom: `1px solid ${T.stroke}`,
      }}
    >
      <Link
        to="/sessions/$sessionId"
        params={{ sessionId }}
        aria-label="Back"
        style={{ color: T.text, display: 'flex', textDecoration: 'none' }}
      >
        <Ico d={I.back} size={20} />
      </Link>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            fontFamily: T.font,
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 10,
            color: T.textMute,
            fontFamily: T.mono,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginTop: 1,
          }}
        >
          {subtitle}
        </div>
      </div>
      <Ico d={I.moreV} size={18} />
    </div>
  );
}

function SectionBadge({ state, index }: { state: 'done' | 'active' | 'todo'; index: number }) {
  const bg = state === 'done' ? T.good : state === 'active' ? T.brand : T.surface2;
  const fg = state === 'todo' ? T.textDim : '#fff';
  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: 10,
        background: bg,
        color: fg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: T.mono,
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {state === 'done' ? <Ico d={I.check2} size={14} sw={2.4} /> : String(index + 1).padStart(2, '0')}
    </div>
  );
}
